import { useMemo } from 'react'

import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useSelectPrimaryIntegration } from '@webapp/hooks/useSelectPrimaryIntegration'
import {
  type IntegrationServicePlatform,
  IntegrationServicePlatforms,
} from '@webapp/models/enums'
import {
  isActiveIntegration,
  isPushIntegration,
  isReadOnlyIntegration,
} from '@webapp/utils/integration/getIntegrationStatus'
import type { Components } from '../../../../api/integrations/client'
import { integrationsKeys } from '../../../../api/integrations/hooks/queryKey.factory'
import { useGetListIntegrations } from '../../../../api/integrations/hooks/useGetListIntegrations'
import type { GetListIntegrationsQueryConfig } from '../../../../api/integrations/hooks/useGetListIntegrations'

export const useGetIntegrationServiceState = (
  config?: GetListIntegrationsQueryConfig
) => {
  const queryClient = useQueryClient()
  const invalidateListQuery = async () =>
    await queryClient.invalidateQueries(integrationsKeys.listIntegrations())

  const { quickbooks_online_sandbox: isQboSandboxEnabled } = useSelector(
    makeSelectFeatureSwitches()
  )

  const listIntegrations = useGetListIntegrations({
    ...config,
  })

  const activeIntegrations = useMemo<
    Components.Schemas.IntegrationResponse[]
  >(() => {
    if (!listIntegrations.data) return []
    return listIntegrations.data.filter(
      (integration) =>
        isActiveIntegration(integration) &&
        (isPushIntegration(integration) || isReadOnlyIntegration(integration))
    )
  }, [listIntegrations.data])

  const primaryIntegration = useSelectPrimaryIntegration(activeIntegrations)

  const getIntegrationByKey = (
    key: IntegrationServicePlatform
  ): Components.Schemas.IntegrationResponse | null => {
    if (!listIntegrations.data || listIntegrations.data.length === 0)
      return null

    return (
      listIntegrations.data.find((integration) => integration.name === key) ||
      null
    )
  }

  const resolveQuickBooksOnlineEnvironment = ():
    | 'urn:integration:backend:codat-quickbooks-online'
    | 'urn:integration:backend:codat-quickbooks-online-sandbox'
    | 'urn:integration:backend:merge:quickbooks-online' => {
    const codatPlatform = isQboSandboxEnabled
      ? IntegrationServicePlatforms.QUICKBOOKS_ONLINE_SANDBOX
      : IntegrationServicePlatforms.QUICKBOOKS_ONLINE

    const codatQboIntegration = getIntegrationByKey(codatPlatform)
    const mergeIntegration = getIntegrationByKey(
      IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE
    )

    if (!codatQboIntegration || !!mergeIntegration) {
      return IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE
    }
    return codatPlatform
  }

  const currentQboEnvironment = resolveQuickBooksOnlineEnvironment()

  return {
    ...listIntegrations,
    invalidateListQuery,
    getIntegrationByKey,
    currentQboEnvironment,
    activeIntegrations,
    primaryIntegration,
  }
}
